// components/SelfPaced/StudentLearningPage.js
// Full-screen learning player — video (react-player), quizzes, documents
// Themed, responsive, supports light & dark mode
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  CheckCircle2, Circle, CirclePlay, Presentation, PenLine, File,
  Play, Trophy, Menu, X, Lock,
  Award, Download, Sparkles,
} from 'lucide-react';
import { AppButton, AppBadge, ProgressBar } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import ContentViewer from './ContentViewer';
import ContentWatermark from '../Common/ContentWatermark';
import { startSession } from '../../utils/sessionTracker';
import useVideoProtection from '../../hooks/useVideoProtection';
import { usePlan } from '../../contexts/PlanContext';
import { getCurrentUser } from '../../utils/auth';
import { isDesktopApp } from '../../utils/desktopApp';
import DesktopAppRequired from '../Common/DesktopAppRequired';
import '../../styles/VideoProtection.scss';
import {
  getCurriculum, getLessonContent,
  updateLessonProgress, getCourseProgress,
  getQuiz, startQuiz, submitQuiz,
  getCertificate,
} from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

const LESSON_ICON = {
  video: <CirclePlay size={15} />,
  presentation: <Presentation size={15} />,
  pdf: <File size={15} />,
  ppt: <Presentation size={15} />,
  docx: <File size={15} />,
  text: <File size={15} />,
  image: <File size={15} />,
  quiz: <PenLine size={15} />,
  document: <File size={15} />,
};

/* ═══════════════════════════════════════════════════════════ */
/* QuizPlayer sub-component                                    */
/* Supports: mcq, multi_select, true_false, short_text         */
/* ═══════════════════════════════════════════════════════════ */
const QuizPlayer = ({ quizId, onComplete, toast }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({}); // { [questionId]: { selectedIndex?, selectedIndices?, textAnswer? } }
  const [attemptId, setAttemptId] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!quizId) return;
    (async () => {
      try {
        setLoading(true);
        const startRes = await startQuiz(quizId);
        const startData = startRes.data;
        // Already passed — show result directly
        if (startData?.completed) {
          const attempt = startData.data;
          setResult({ passed: true, percentage: attempt?.percentage ?? 100, score: attempt?.score, totalMarks: attempt?.totalMarks });
          setLoading(false);
          return;
        }
        const attempt = startData?.data || startData;
        setAttemptId(attempt?._id || null);
        const res = await getQuiz(quizId);
        setQuiz(res.data?.data || res.data);
        setAnswers({});
        setResult(null);
      } catch (err) {
        const errData = err?.response?.data;
        // Max attempts reached — show last result
        if (errData?.maxAttemptsReached && errData?.data) {
          const attempt = errData.data;
          setResult({ passed: attempt.passed, percentage: attempt.percentage ?? 0, score: attempt.score, totalMarks: attempt.totalMarks, maxAttemptsReached: true });
        } else {
          toast('Failed to load quiz.', 'error');
        }
      }
      finally { setLoading(false); }
    })();
  }, [quizId, toast, retryKey]);

  // ── Answer handlers per type ──
  const setMcqAnswer = (qId, optIndex) => {
    setAnswers(a => ({ ...a, [qId]: { selectedIndex: optIndex } }));
  };

  const toggleMultiSelect = (qId, optIndex) => {
    setAnswers(a => {
      const prev = a[qId]?.selectedIndices || [];
      const next = prev.includes(optIndex) ? prev.filter(x => x !== optIndex) : [...prev, optIndex];
      return { ...a, [qId]: { selectedIndices: next } };
    });
  };

  const setTextAnswer = (qId, text) => {
    setAnswers(a => ({ ...a, [qId]: { textAnswer: text } }));
  };

  const handleSubmit = async () => {
    if (!quiz) return;
    setSubmitting(true);
    try {
      const questions = quiz.questions || [];
      const formattedAnswers = questions.map((q) => {
        const qId = q._id;
        const ans = answers[qId] || {};
        const qType = q.questionType || 'mcq';

        const base = { questionId: qId };
        if (qType === 'mcq' || qType === 'true_false') {
          return { ...base, selectedIndex: ans.selectedIndex ?? -1 };
        }
        if (qType === 'multi_select') {
          return { ...base, selectedIndices: ans.selectedIndices || [] };
        }
        if (qType === 'short_text') {
          return { ...base, textAnswer: ans.textAnswer || '' };
        }
        return { ...base, selectedIndex: ans.selectedIndex ?? -1 };
      });

      const res = await submitQuiz(quizId, { attemptId, answers: formattedAnswers });
      const data = res.data?.data || res.data;
      setResult(data);
      if (data?.passed) onComplete?.();
    } catch { toast('Failed to submit quiz.', 'error'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="spl-quiz spl-quiz--loading"><Spinner animation="border" variant="secondary" /></div>;

  // Result view (completed, submitted, or max attempts)
  if (result) {
    return (
      <div className="spl-quiz">
        <div className={`spl-quiz__result ${result.passed ? 'spl-quiz__result--pass' : 'spl-quiz__result--fail'}`}>
          <Trophy size={32} />
          <h3>{result.passed ? 'Congratulations!' : 'Keep Trying'}</h3>
          <p>Score: <strong>{result.percentage ?? 0}%</strong> {result.passed ? '— Passed' : `— Need ${quiz?.passingScore || 50}% to pass`}</p>
          {result.maxAttemptsReached && <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>All attempts used.</p>}
          {!result.passed && !result.maxAttemptsReached && (
            <AppButton variant="outline" size="sm" onClick={() => { setResult(null); setAnswers({}); setQuiz(null); setAttemptId(null); setRetryKey(k => k + 1); }}>Retry Quiz</AppButton>
          )}
        </div>
      </div>
    );
  }

  if (!quiz) return <div className="spl-quiz"><p>Quiz unavailable.</p></div>;

  const questions = quiz.questions || [];

  // ── Check if all questions answered ──
  const allAnswered = questions.every((q) => {
    const qId = q._id;
    const ans = answers[qId];
    if (!ans) return false;
    const qType = q.questionType || 'mcq';
    if (qType === 'mcq' || qType === 'true_false') return ans.selectedIndex !== undefined && ans.selectedIndex >= 0;
    if (qType === 'multi_select') return (ans.selectedIndices || []).length > 0;
    if (qType === 'short_text') return !!(ans.textAnswer?.trim());
    return false;
  });

  // Result view — handled above (before quiz null check)

  return (
    <div className="spl-quiz">
      <h3 className="spl-quiz__title">{quiz.title || 'Quiz'}</h3>
      {quiz.description && <p className="spl-quiz__desc">{quiz.description}</p>}

      <div className="spl-quiz__questions">
        {questions.map((q, qi) => {
          const qId = q._id || qi;
          const qType = q.questionType || 'mcq';
          const ans = answers[qId] || {};
          return (
            <div key={qId} className="spl-quiz__q">
              <p className="spl-quiz__q-text"><strong>Q{qi + 1}.</strong> {q.question || q.text}</p>

              {/* MCQ & True/False — radio buttons */}
              {(qType === 'mcq' || qType === 'true_false') && (
                <div className="spl-quiz__options">
                  {(q.options || []).map((opt, oi) => {
                    const optValue = typeof opt === 'string' ? opt : opt.text || opt.label;
                    const selected = ans.selectedIndex === oi;
                    return (
                      <label key={oi} className={`spl-quiz__option ${selected ? 'spl-quiz__option--selected' : ''}`}>
                        <input
                          type="radio"
                          name={`q-${qId}`}
                          checked={selected}
                          onChange={() => setMcqAnswer(qId, oi)}
                        />
                        <span>{optValue}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Multi-select — checkboxes */}
              {qType === 'multi_select' && (
                <div className="spl-quiz__options">
                  <p className="spl-quiz__hint">Select all that apply:</p>
                  {(q.options || []).map((opt, oi) => {
                    const optValue = typeof opt === 'string' ? opt : opt.text || opt.label;
                    const selected = (ans.selectedIndices || []).includes(oi);
                    return (
                      <label key={oi} className={`spl-quiz__option ${selected ? 'spl-quiz__option--selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleMultiSelect(qId, oi)}
                        />
                        <span>{optValue}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Short text — text input */}
              {qType === 'short_text' && (
                <div className="spl-quiz__text-answer">
                  <input
                    type="text"
                    className="spl-quiz__text-input"
                    value={ans.textAnswer || ''}
                    onChange={(e) => setTextAnswer(qId, e.target.value)}
                    placeholder="Type your answer here..."
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <AppButton onClick={handleSubmit} disabled={submitting || !allAnswered}>
        {submitting ? <><Spinner size="sm" animation="border" /> Submitting…</> : 'Submit Quiz'}
      </AppButton>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/* Course Completion Banner                                    */
/* ═══════════════════════════════════════════════════════════ */
const CourseCompletionBanner = ({ courseId, progress, onGetCert, toast }) => {
  const navigate = useNavigate();
  const [certData, setCertData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (progress < 100 || dismissed) return null;

  const handleGetCertificate = async () => {
    setGenLoading(true);
    try {
      const res = await getCertificate(courseId);
      const data = res.data?.data || res.data;
      setCertData(data);
      const url = data?.downloadUrl || data?.url;
      if (url) {
        setPdfUrl(url);
        toast('Certificate generated!', 'success');
      } else {
        toast('Certificate generated! Visit My Certificates to download.', 'success');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Unable to generate certificate. You may need to pass the final exam first.';
      toast(msg, 'error');
    } finally {
      setGenLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    const url = pdfUrl || certData?.downloadUrl || certData?.url;
    if (!url) return;
    try {
      setDownloading(true);
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${certData?.certificateNo || 'certificate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch { toast('Download failed', 'error'); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <div className="spl-completion-banner">
        <div className="spl-completion-banner__confetti">
          <Sparkles size={20} />
        </div>
        <div className="spl-completion-banner__content">
          <div className="spl-completion-banner__icon">
            <Trophy size={28} />
          </div>
          <div className="spl-completion-banner__text">
            <h3>Congratulations! You've completed this course!</h3>
            <p>You've finished all lessons. Claim your certificate of completion.</p>
          </div>
          <div className="spl-completion-banner__actions">
            {certData ? (
              <>
                <AppButton size="sm" onClick={handleDownloadPdf} disabled={downloading}>
                  {downloading ? <Spinner size="sm" animation="border" /> : <><Download size={14} /> Download PDF</>}
                </AppButton>
                <AppButton size="sm" variant="outline" onClick={() => navigate('/s/self-paced/certificates')}>
                  <Award size={14} /> My Certificates
                </AppButton>
              </>
            ) : (
              <AppButton size="sm" onClick={handleGetCertificate} disabled={genLoading}>
                {genLoading ? <><Spinner size="sm" animation="border" /> Generating...</> : <><Award size={14} /> Get Certificate</>}
              </AppButton>
            )}
            <button className="spl-completion-banner__dismiss" onClick={() => setDismissed(true)}>
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* In-app certificate viewer */}
      {pdfUrl && (
        <div className="spl-cert-viewer">
          <div className="spl-cert-viewer__header">
            <span><Award size={16} /> Your Certificate</span>
            <div className="spl-cert-viewer__actions">
              <AppButton size="sm" onClick={handleDownloadPdf} disabled={downloading}>
                {downloading ? <Spinner size="sm" animation="border" /> : <><Download size={14} /> Download</>}
              </AppButton>
              <button className="spl-cert-viewer__close" onClick={() => setPdfUrl(null)}>
                <X size={16} />
              </button>
            </div>
          </div>
          <iframe src={pdfUrl} title="Certificate" className="spl-cert-viewer__pdf" />
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/* Sidebar Curriculum                                          */
/* ═══════════════════════════════════════════════════════════ */
const Sidebar = ({ modules, currentLessonId, completedSet, onSelect, progress, expanded, setExpanded }) => {
  const [openModules, setOpenModules] = useState(() => {
    // Open the module that contains the current lesson
    const idx = modules.findIndex(m => (m.lessons || []).some(l => l._id === currentLessonId));
    return new Set([idx >= 0 ? idx : 0]);
  });

  const toggleModule = (i) => setOpenModules(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });

  return (
    <div className={`spl-sidebar ${expanded ? 'spl-sidebar--open' : ''}`}>
      <div className="spl-sidebar__header">
        <h4>Course Content</h4>
        <span className="spl-sidebar__progress">{Math.round(progress)}% complete</span>
        <button className="spl-sidebar__close" onClick={() => setExpanded(false)}><X size={18} /></button>
      </div>
      <ProgressBar value={progress} color="emerald" size="sm" className="spl-sidebar__bar" />

      <div className="spl-sidebar__modules">
        {modules.map((mod, mi) => {
          const lessons = mod.lessons || [];
          const isOpen = openModules.has(mi);
          const doneCount = lessons.filter(l => completedSet.has(l._id)).length;
          const isLocked = mod.isLocked;
          return (
            <div key={mod._id || mi} className={`spl-sidebar__module ${isLocked ? 'spl-sidebar__module--locked' : ''}`}>
              <button className="spl-sidebar__module-header" onClick={() => toggleModule(mi)}>
                <span className="spl-sidebar__module-title">
                  {isLocked && <Lock size={14} className="spl-sidebar__lock-icon" />}
                  {mod.title || mod.name}
                </span>
                <span className="spl-sidebar__module-meta">{doneCount}/{lessons.length} {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
              </button>
              {isLocked && isOpen && (
                <div className="spl-sidebar__lock-msg">
                  <Lock size={12} /> {mod.lockReason || 'Complete the previous module to unlock.'}
                </div>
              )}
              {isOpen && (
                <ul className="spl-sidebar__lessons">
                  {lessons.map((l) => {
                    const isActive = l._id === currentLessonId;
                    const isDone = completedSet.has(l._id);
                    return (
                      <li
                        key={l._id}
                        className={`spl-sidebar__lesson ${isActive ? 'spl-sidebar__lesson--active' : ''} ${isDone ? 'spl-sidebar__lesson--done' : ''} ${isLocked ? 'spl-sidebar__lesson--locked' : ''}`}
                        onClick={() => !isLocked && onSelect(l)}
                      >
                        <span className="spl-sidebar__lesson-check">
                          {isLocked ? <Lock size={15} /> : isDone ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                        </span>
                        <span className="spl-sidebar__lesson-icon">{LESSON_ICON[l.type] || LESSON_ICON.video}</span>
                        <span className="spl-sidebar__lesson-title">{l.title || l.name}</span>
                        {l.duration && <span className="spl-sidebar__lesson-dur">{l.duration}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/* Main Learning Page                                          */
/* ═══════════════════════════════════════════════════════════ */
export default function StudentLearningPage() {
  const { id } = useParams();          // course id
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();

  const [modules, setModules] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [completedSet, setCompletedSet] = useState(new Set());
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [courseThumbnailUrl, setCourseThumbnailUrl] = useState('');
  const [courseVideoSecurity, setCourseVideoSecurity] = useState(false);
  const videoRef = useRef(null);


  // Desktop app required only when plan+institute enabled AND course has videoSecurity on
  const requireDesktopApp = hasPlanFeature('videoSecurityEnabled') && courseVideoSecurity && !isDesktopApp() && currentUser?.usertype === 'student';
  const progressIntervalRef = useRef(null);
  const splPlayerRef = useRef(null);
  const [resumePosition, setResumePosition] = useState(0);

  // ─── Session time tracking ───
  useEffect(() => {
    if (!currentLesson) return;
    const type = currentLesson.type === 'quiz' ? 'quiz'
      : currentLesson.type === 'video' ? 'video'
      : 'document';
    const tracker = startSession({
      activityType: type === 'document' ? 'self_paced' : type,
      referenceId: currentLesson._id,
      referenceTitle: currentLesson.title || currentLesson.name || 'Lesson',
    });
    return () => tracker.end();
  }, [currentLesson?._id]);

  /* shortcuts */
  const allLessons = useMemo(() => modules.flatMap(m => m.lessons || []), [modules]);
  const currentIndex = useMemo(() => allLessons.findIndex(l => l._id === currentLesson?._id), [allLessons, currentLesson]);
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 && currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  const showToast = useCallback((message, type = 'success') => setToast({ show: true, message, type }), []);

  /* ── Load curriculum + progress ──────────────────────── */
  const loadCourse = useCallback(async () => {
    try {
      setLoading(true);
      const [curRes, progRes] = await Promise.allSettled([
        getCurriculum(id),
        getCourseProgress(id),
      ]);
      const curData = curRes.status === 'fulfilled' ? curRes.value.data : null;
      const mods = curData?.data || curData || [];
      const prog = progRes.status === 'fulfilled' ? (progRes.value.data?.data || progRes.value.data) : null;
      setModules(mods);

      // Resolve course thumbnail with sessionStorage caching (50 min)
      const cacheKey = `course_thumb_${id}`;
      const freshThumb = curData?.courseThumbnailUrl || '';
      if (freshThumb) {
        setCourseThumbnailUrl(freshThumb);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ url: freshThumb, exp: Date.now() + 50 * 60 * 1000 })); } catch (_) {}
      } else {
        try {
          const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
          if (cached && cached.exp > Date.now()) setCourseThumbnailUrl(cached.url);
        } catch (_) {}
      }

      // Build completed set
      const doneIds = new Set();
      if (Array.isArray(prog?.completedLessonIds)) prog.completedLessonIds.forEach(lid => doneIds.add(typeof lid === 'object' ? lid._id : lid));
      if (Array.isArray(prog?.completedLessons)) prog.completedLessons.forEach(lid => doneIds.add(typeof lid === 'object' ? lid._id : lid));
      if (Array.isArray(prog?.lessonProgress)) prog.lessonProgress.filter(lp => lp.completed).forEach(lp => doneIds.add(lp.lesson?._id || lp.lesson));
      setCompletedSet(doneIds);
      setProgress(prog?.completionPct ?? prog?.percentage ?? prog?.progress ?? 0);

      // Pick first uncompleted lesson from an unlocked module, or first lesson in first unlocked module
      const unlockedMods = mods.filter(m => !m.isLocked);
      const flat = unlockedMods.flatMap(m => m.lessons || []);
      const first = flat.find(l => !doneIds.has(l._id)) || flat[0];
      if (first) openLesson(first);
    } catch {
      showToast('Failed to load course content.', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  /* ── Open a lesson ───────────────────────────────────── */
  const openLesson = async (lesson) => {
    // Check if lesson belongs to a locked module
    const parentMod = modules.find(m => (m.lessons || []).some(l => l._id === lesson._id));
    if (parentMod?.isLocked) {
      showToast(parentMod.lockReason || 'This module is locked. Complete the previous module first.', 'error');
      return;
    }

    setCurrentLesson(lesson);
    setLessonContent(null);
    setResumePosition(0);
    setSidebarExpanded(false);
    if (lesson.type === 'quiz') return; // quiz handled by QuizPlayer

    setContentLoading(true);
    try {
      const res = await getLessonContent(lesson._id);
      const data = res.data?.data || res.data;
      setLessonContent(data);
      if (data?.videoSecurity !== undefined) setCourseVideoSecurity(!!data.videoSecurity);
      if (lesson.type === 'video' && data?.lastPosition > 0) {
        setResumePosition(data.lastPosition);
      }
    } catch {
      showToast('Failed to load lesson content.', 'error');
    } finally {
      setContentLoading(false);
    }
  };

  /* ── Mark complete ───────────────────────────────────── */
  const markComplete = useCallback(async () => {
    if (!currentLesson) return;
    if (completedSet.has(currentLesson._id)) return; // already done
    try {
      await updateLessonProgress(currentLesson._id, { completed: true });
      setCompletedSet(s => new Set([...s, currentLesson._id]));

      // Recalculate progress
      const total = allLessons.length;
      const done = [...completedSet, currentLesson._id].length;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      setProgress(pct);
    } catch {
      // silently fail — auto-complete shouldn't disrupt the student
    }
  }, [currentLesson, completedSet, allLessons]);

  /* ── Video progress auto-save (every 10s) ────────────── */
  const handleVideoReady = useCallback(() => {
    if (resumePosition > 0 && videoRef.current) {
      videoRef.current.currentTime = resumePosition;
    }
  }, [resumePosition]);

  // Periodic progress save via timeupdate
  const handleTimeUpdate = useCallback(() => {
    if (!currentLesson || currentLesson.type !== 'video' || !videoRef.current) return;
    const playedSeconds = videoRef.current.currentTime;
    if (!progressIntervalRef.current) progressIntervalRef.current = 0;
    const diff = Math.abs(playedSeconds - progressIntervalRef.current);
    if (diff >= 10) {
      progressIntervalRef.current = playedSeconds;
      updateLessonProgress(currentLesson._id, {
        lastPosition: Math.floor(playedSeconds),
        watchTime: Math.floor(playedSeconds),
      }).catch(() => {});
    }
  }, [currentLesson]);

  // Save position on unmount / lesson change
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current > 0 && currentLesson?.type === 'video') {
        updateLessonProgress(currentLesson._id, {
          lastPosition: Math.floor(progressIntervalRef.current),
          watchTime: Math.floor(progressIntervalRef.current),
        }).catch(() => {});
      }
    };
  }, [currentLesson]);

  /* ── Build video URL ─────────────────────────────────── */
  const videoUrl = useMemo(() => {
    if (!currentLesson || currentLesson.type !== 'video') return null;
    // If lesson content has a direct URL (backend returns contentUrl for videos)
    if (lessonContent?.contentUrl) return lessonContent.contentUrl;
    if (lessonContent?.videoUrl) return lessonContent.videoUrl;
    if (lessonContent?.url) return lessonContent.url;
    return null;
  }, [currentLesson, lessonContent]);

  /* ── Auto-refresh expired video URL ─────────────────── */
  const handleVideoError = useCallback(async () => {
    if (!currentLesson || currentLesson.type !== 'video') return;
    const pos = videoRef.current?.currentTime || progressIntervalRef.current || 0;
    if (pos === 0) return; // don't retry if never started
    try {
      const res = await getLessonContent(currentLesson._id);
      const data = res.data?.data || res.data;
      setLessonContent(data);
      // Seek back after URL refreshes
      if (pos > 0) {
        setTimeout(() => {
          if (videoRef.current) videoRef.current.currentTime = pos;
        }, 500);
      }
    } catch {}
  }, [currentLesson]);

  /* ── Video protection ─────────────────────────────── */
  useVideoProtection({
    containerRef: splPlayerRef,
    onPause: useCallback(() => { videoRef.current?.pause(); }, []),
    onResume: useCallback(() => {}, []),
    active: currentLesson?.type === 'video' && !!videoUrl,
  });



  /* ── Render ────────────────────────────────────────── */
  if (loading) {
    return <div className="sp-page spl-page"><div className="sp-loading"><Spinner animation="border" variant="secondary" /></div></div>;
  }

  const isDone = currentLesson && completedSet.has(currentLesson._id);

  return (
    <div className="sp-page spl-page">
      {/* Top bar */}
      <div className="spl-topbar">
        <button className="spl-topbar__back" onClick={() => navigate(`/s/self-paced/${id}`)}>
          <ArrowLeft size={18} /> <span className="spl-topbar__back-text">Back to Course</span>
        </button>
        <div className="spl-topbar__title">{currentLesson?.title || currentLesson?.name || 'Lesson'}</div>
        <div className="spl-topbar__actions">
          <ProgressBar value={progress} color="emerald" size="sm" className="spl-topbar__progress" />
          <span className="spl-topbar__pct">{Math.round(progress)}%</span>
          <button className="spl-topbar__menu" onClick={() => setSidebarExpanded(e => !e)}><Menu size={20} /></button>
        </div>
      </div>

      {/* Course Completion Banner */}
      <CourseCompletionBanner
        courseId={id}
        progress={progress}
        onGetCert={() => {}}
        toast={showToast}
      />

      <div className="spl-layout">
        {/* Content Area */}
        <div className="spl-content">
          {/* Video Player */}
          {currentLesson?.type === 'video' && (
            requireDesktopApp ? (
              <DesktopAppRequired />
            ) : (
            <div
              className="spl-player vp-protected"
              ref={splPlayerRef}
            >
              <ContentWatermark videoMode />
              {contentLoading ? (
                <div className="spl-player__loading"><Spinner animation="border" variant="light" /></div>
              ) : videoUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    controlsList="nodownload nofullscreen"
                    disablePictureInPicture
                    src={videoUrl}
                    onLoadedData={handleVideoReady}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={markComplete}
                    onContextMenu={e => e.preventDefault()}
                    onError={handleVideoError}
                  />
              ) : (
                <div className="spl-player__empty">
                  <Play size={32} />
                  <p>Video not available</p>
                </div>
              )}
            </div>
            )
          )}

          {/* Presentation / Document / PDF / DOCX / Image / Text — Universal Viewer */}
          {currentLesson && currentLesson.type !== 'video' && currentLesson.type !== 'quiz' && (
            <ContentViewer
              type={currentLesson.type}
              fileType={currentLesson.fileType || lessonContent?.fileType}
              url={lessonContent?.contentUrl || lessonContent?.fileUrl || lessonContent?.url}
              pdfUrl={lessonContent?.pdfContentUrl}
              content={lessonContent?.content || currentLesson?.content}
              html={lessonContent?.html}
              title={currentLesson.title || currentLesson.name}
              fileName={currentLesson.fileName || lessonContent?.fileName}
              loading={contentLoading}
              lessonId={currentLesson._id}
              onComplete={markComplete}
            />
          )}

          {/* Quiz */}
          {currentLesson?.type === 'quiz' && (
            <QuizPlayer
              quizId={currentLesson.quiz || currentLesson.quizId || currentLesson._id}
              onComplete={markComplete}
              toast={showToast}
            />
          )}

          {/* Lesson actions */}
          <div className="spl-actions">
            <AppButton variant="outline" size="sm" disabled={!prevLesson} onClick={() => prevLesson && openLesson(prevLesson)}>
              <ChevronLeft size={15} /> Previous
            </AppButton>

            {isDone && (
              <AppBadge color="emerald"><CheckCircle2 size={13} /> Completed</AppBadge>
            )}

            <AppButton variant={nextLesson ? 'primary' : 'outline'} size="sm" disabled={!nextLesson} onClick={() => nextLesson && openLesson(nextLesson)}>
              Next <ChevronRight size={15} />
            </AppButton>
          </div>
        </div>

        {/* Sidebar */}
        <Sidebar
          modules={modules}
          currentLessonId={currentLesson?._id}
          completedSet={completedSet}
          onSelect={openLesson}
          progress={progress}
          expanded={sidebarExpanded}
          setExpanded={setSidebarExpanded}
        />
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarExpanded && <div className="spl-overlay" onClick={() => setSidebarExpanded(false)} />}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
